/**
 * Delayed Image Loading Utility
 *
 * Delays image loading and expensive operations (RPC calls, API requests) until after
 * the critical path completes. Operations will load after:
 * - A minimum delay (2 seconds) - ensures critical path has time to complete
 * - requestIdleCallback (if available) - defers to browser idle periods to minimize main thread work
 * - Page load event + 1 second buffer - ensures critical path completes even if page loads quickly
 * - User interaction (scroll, click, touch) - immediate to improve perceived performance
 *
 * Whichever comes first. This helps minimize main thread work per Lighthouse recommendations:
 * https://developer.chrome.com/docs/lighthouse/performance/mainthread-work-breakdown/
 */

let imageLoadingEnabled = false
let enablePromise: Promise<void> | null = null

/**
 * Enable image loading after delay and/or events
 * This function is idempotent - safe to call multiple times
 */
export function enableImageLoading(): void {
  // Already enabled, nothing to do
  if (imageLoadingEnabled) {
    return
  }

  // Already initializing, don't create another promise
  if (enablePromise) {
    return
  }

  // Create the promise synchronously to ensure it exists before any await
  enablePromise = (async () => {
    try {
      // Wait for minimum delay (2 seconds) to ensure critical path completes
      // Reduced from 5s to 2s to minimize main thread blocking while still allowing
      // critical rendering to complete. According to Lighthouse docs, we want to
      // minimize main thread work while ensuring the critical path isn't blocked.
      const minDelay = new Promise(resolve => setTimeout(resolve, 2000))

      // Use requestIdleCallback if available for better main thread utilization
      // Falls back to setTimeout if not available (Safari, older browsers)
      const idleCallback =
        'requestIdleCallback' in window
          ? new Promise<void>(resolve => {
              // Type assertion needed because requestIdleCallback is not in standard Window type
              // but is available in modern browsers
              const windowWithIdleCallback = window as Window &
                typeof globalThis & {
                  requestIdleCallback: (
                    callback: () => void,
                    options?: { timeout?: number }
                  ) => number
                }
              windowWithIdleCallback.requestIdleCallback(() => resolve(), { timeout: 2000 })
            })
          : new Promise(resolve => setTimeout(resolve, 2000))

      const pageLoad = new Promise<void>(resolve => {
        // Only resolve page load after a minimum delay to ensure critical path is done
        if (document.readyState === 'complete') {
          // Page already loaded, but wait additional time for critical path
          setTimeout(() => resolve(), 1000)
        } else {
          window.addEventListener(
            'load',
            () => {
              // Page just loaded, wait additional time for critical path
              setTimeout(() => resolve(), 1000)
            },
            { once: true }
          )
        }
      })

      // Also enable on user interaction (scroll, click, touch) - immediate
      const userInteraction = new Promise<void>(resolve => {
        const enable = () => {
          resolve()
          window.removeEventListener('scroll', enable, true)
          window.removeEventListener('click', enable, true)
          window.removeEventListener('touchstart', enable, true)
        }
        window.addEventListener('scroll', enable, true)
        window.addEventListener('click', enable, true)
        window.addEventListener('touchstart', enable, true)
      })

      // Enable when any condition is met
      // User interaction is immediate, but page load, idleCallback, and minDelay ensure critical path completes
      // Using requestIdleCallback helps minimize main thread work by deferring to idle periods
      await Promise.race([minDelay, idleCallback, pageLoad, userInteraction])
      imageLoadingEnabled = true
    } catch (error) {
      // If something goes wrong, enable anyway to prevent blocking
      console.warn('Error in enableImageLoading:', error)
      imageLoadingEnabled = true
    }
  })()
}

/**
 * Check if image loading is enabled
 */
export function isImageLoadingEnabled(): boolean {
  return imageLoadingEnabled
}

/**
 * Wait for image loading to be enabled
 * Lazy initialization - only starts the delay mechanism when first called
 */
export async function waitForImageLoading(): Promise<void> {
  // If already enabled, return immediately
  if (imageLoadingEnabled) {
    return
  }

  // If promise doesn't exist, create it
  if (!enablePromise) {
    enableImageLoading()
  }

  // Wait for the promise to resolve
  // This ensures we don't proceed until image loading is enabled
  if (enablePromise) {
    await enablePromise
  }
}

// Don't initialize immediately - let it initialize lazily when waitForImageLoading is first called
// This prevents the initialization code from being executed at module load time
